import axios from "axios";
import { API } from "./store";

let _aliases = null;

export async function loadAliases() {
  if (_aliases) return _aliases;
  try {
    const r = await axios.get(`${API}/voice/aliases`);
    _aliases = r.data.aliases || [];
  } catch (e) {
    console.debug("aliases load failed:", e?.message);
    _aliases = [];
  }
  return _aliases;
}

export const invalidateAliases = () => { _aliases = null; };

export const norm = (s) => (s || "").toLowerCase().replace(/[^\p{L}\p{M}\p{N}\s]/gu, " ").replace(/\s+/g, " ").trim();

export function logVoiceMiss(phrase, expanded, lang, path) {
  axios.post(`${API}/voice/misses`, { phrase, expanded, lang, path }).catch((e) => console.debug("miss log:", e?.message));
}

export function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

export function similarity(a, b) {
  const m = Math.max(a.length, b.length);
  return m ? 1 - levenshtein(a, b) / m : 1;
}

const fuzzyThreshold = (word) => (word.length <= 3 ? 1 : word.length <= 5 ? 0.8 : 0.75);

const STOPWORDS = new Set(["for", "the", "a", "an", "me", "of", "in", "with", "some", "please", "my", "to", "and", "or", "our", "your"]);

// Replace spoken aliases (multilingual) with catalog-canonical terms
export function expandQuery(phrase, aliases) {
  let out = ` ${norm(phrase)} `;
  const entries = [];
  for (const e of aliases) {
    if (e.type === "world") continue;
    for (const al of e.aliases || []) entries.push({ al: norm(al), canonical: e.canonical });
  }
  entries.sort((x, y) => y.al.length - x.al.length);
  for (const { al, canonical } of entries) {
    if (al && out.includes(` ${al} `)) out = out.split(` ${al} `).join(` ${canonical} `);
  }
  const tokens = out.trim().split(" ").filter((t) => t && !STOPWORDS.has(t));
  const mapped = tokens.map((t) => {
    let best = { s: 0, canonical: null };
    for (const { al, canonical } of entries) {
      if (al.includes(" ") || t === canonical) continue;
      if (t === al) return canonical;
      const s = similarity(t, al);
      if (s > best.s) best = { s, canonical };
    }
    return best.canonical && best.s >= fuzzyThreshold(t) ? best.canonical : t;
  });
  return [...new Set(mapped)].join(" ");
}

export function resolveWorld(phrase, aliases, worlds) {
  const p = norm(phrase);
  const candidates = worlds.map((w) => ({ text: norm(w.title), slug: w.slug }));
  for (const e of aliases.filter((a) => a.type === "world")) {
    for (const al of e.aliases || []) candidates.push({ text: norm(al), slug: e.canonical });
  }
  let best = { s: 0, slug: null };
  for (const c of candidates) {
    if (!c.text) continue;
    if (p.includes(c.text)) return { slug: c.slug, score: 1 };
    const s = similarity(p, c.text);
    if (s > best.s) best = { s, slug: c.slug };
  }
  return best.s >= 0.72 ? { slug: best.slug, score: best.s } : null;
}

// Top-N closest catalog terms for "did you mean" suggestions
export function topSuggestions(phrase, aliases, count = 3) {
  const p = norm(phrase);
  const tokens = p.split(" ").filter(Boolean);
  const scored = new Map();
  for (const e of aliases.filter((a) => a.type !== "world")) {
    let s = similarity(p, norm(e.canonical));
    const texts = [norm(e.canonical), ...(e.aliases || []).map(norm)];
    for (const t of texts) {
      s = Math.max(s, similarity(p, t));
      for (const tok of tokens) s = Math.max(s, similarity(tok, t));
    }
    scored.set(e.canonical, Math.max(scored.get(e.canonical) || 0, s));
  }
  return [...scored.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([canonical, score]) => ({ canonical, score }));
}
